<script lang="ts">
  import Message from '@pin/components/Message.svelte';
  import type {
    AdminMessage,
    Overlay,
    ClientToServerEvent as OverlayClientToServerEvents,
    ServerToClientEvents as OverlayServerToClientEvents,
    OverlayState,
  } from '@pin/schemas/overlay';
  import type { Socket } from 'socket.io-client';
  import { tick } from 'svelte';
  import { flip } from 'svelte/animate';
  import { quintOut } from 'svelte/easing';
  import { crossfade } from 'svelte/transition';
  import Grip from '../Icons/Grip.svelte';
  import Star from '../Icons/Star.svelte';

  interface Props {
    overlaySocket: Socket<
      OverlayServerToClientEvents,
      OverlayClientToServerEvents
    > | null;
    presenter?: boolean;
    /** Without it the names are plain text, as in the presenter view. */
    onUserClick?: (user: Overlay['user']) => void;
  }

  let { overlaySocket, presenter = false, onUserClick }: Props = $props();

  let live = $state<OverlayState['live'] | null>();
  let candidates = $state<OverlayState['candidates']>([]);
  let archive = $state<OverlayState['archive']>([]);
  let liveSeconds = $state(0);

  const animationDuration = 250;
  const [send, receive] = crossfade({
    fallback(node, _params) {
      const style = getComputedStyle(node);
      const transform = style.transform === 'none' ? '' : style.transform;

      return {
        duration: animationDuration,
        easing: quintOut,
        css: (t) => `
					transform: ${transform} scale(${t});
					opacity: ${t}
				`,
      };
    },
  });

  $effect(() => {
    const liveAt = live?.liveAt;
    if (liveAt == null) {
      liveSeconds = 0;
      return;
    }

    // The count changes once a second, so wake up on the next whole second
    // instead of every frame. The positive modulo keeps the boundary when the
    // browser's clock is behind the server's and the elapsed time is negative.
    let timeoutId: ReturnType<typeof setTimeout>;
    const tick = () => {
      const elapsedMs = Date.now() - liveAt;
      liveSeconds = Math.floor(elapsedMs / 1000);
      timeoutId = setTimeout(tick, 1000 - (((elapsedMs % 1000) + 1000) % 1000));
    };

    tick();

    return () => clearTimeout(timeoutId);
  });

  const hide = () => overlaySocket?.emit('CHANGE', { type: 'OVERLAY_HIDE' });

  const show = (message: Overlay) =>
    overlaySocket?.emit('CHANGE', { type: 'OVERLAY_SHOW', message });

  const toggleOverlayPin = (id: string) =>
    overlaySocket?.emit('CHANGE', {
      type: 'OVERLAY_PIN_TOGGLE',
      id,
    });

  const remove = (id: string) =>
    overlaySocket?.emit('CHANGE', { type: 'OVERLAY_REMOVE_CANDIDATE', id });

  // Pinned first, otherwise in the server's order. Stable, so the order
  // within each group is the server's.
  const saved = $derived(
    [...candidates].sort((a, b) => (b.pin ? 1 : 0) - (a.pin ? 1 : 0))
  );

  // Svelte reorders the list by moving the messages in the DOM, and the
  // browser takes the focus off an element it moves. keepFocus puts it back
  // on the message that had it after the list changes, and scrolls to it if
  // it moved; a blur while the list changes does not count as leaving it.
  let savedList = $state<HTMLElement>();
  let focusedId: string | null = null;
  let reordering = false;
  // Whether the message moved: its place among the messages saved both
  // before and after an update, in the same group both times, changed, or
  // its own group did. A message saved, shown, removed or pinned next to it
  // does not move it.
  const movedSince = (before: Overlay[], id: string) => {
    const pinBefore = new Map(before.map((c) => [c.id, c.pin ?? false]));
    const pinAfter = new Map(saved.map((c) => [c.id, c.pin ?? false]));
    if (pinBefore.get(id) !== pinAfter.get(id)) {
      return true;
    }
    const placeIn = (list: Overlay[]) =>
      list
        .filter((c) => pinBefore.get(c.id) === pinAfter.get(c.id))
        .findIndex((c) => c.id === id);
    return placeIn(before) !== placeIn(saved);
  };
  const keepFocus = async (before: Overlay[]) => {
    // The message or one of its buttons, before Svelte moves it
    const focused = document.activeElement;
    reordering = true;
    await tick();
    reordering = false;
    if (focusedId == null) {
      return;
    }
    // Shown or removed: forget it, so saving it again does not take focus
    if (!saved.some((c) => c.id === focusedId)) {
      focusedId = null;
      return;
    }
    const node = savedList?.querySelector<HTMLElement>(
      `[data-id="${CSS.escape(focusedId)}"]`
    );
    if (node == null) {
      return;
    }
    if (document.activeElement === document.body) {
      const target =
        focused instanceof HTMLElement && node.contains(focused)
          ? focused
          : node;
      target.focus({ preventScroll: true });
    }
    // After the flip, which draws the message at its old place until then
    if (
      node.contains(document.activeElement) &&
      movedSince(before, focusedId)
    ) {
      setTimeout(() => {
        if (node.contains(document.activeElement)) {
          node.scrollIntoView({ block: 'nearest' });
        }
      }, animationDuration);
    }
  };

  // Arrow up or down on a focused saved message moves it one place among the
  // pinned or the unpinned messages. The server picks the neighbour from the
  // order it has, so a key held down moves it one place per press however
  // late the answers come.
  const moveSaved = (event: KeyboardEvent, id: string) => {
    const direction =
      event.key === 'ArrowUp'
        ? 'up'
        : event.key === 'ArrowDown'
          ? 'down'
          : null;
    if (
      direction == null ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    ) {
      return;
    }
    event.preventDefault();
    overlaySocket?.emit('CHANGE', {
      type: 'OVERLAY_MOVE_CANDIDATE',
      id,
      direction,
    });
  };

  // Dragging a saved message by its handle drops it before or after another
  // message in its group, the pinned or the unpinned. The list does not
  // change until the server's state comes back; the messages around the
  // drop move apart to a slot as high as the dragged one, which closes up
  // where it was, so the place is open and the drag image does not cover it.
  // The server places it next to the message it was dropped on, so a list
  // that changed during the drag still puts it there.
  type Drop = { position: 'before' | 'after'; anchor: string };
  // Firefox starts no drag without data. A type of our own, so the drag
  // pastes nothing into a text field, and a drag from elsewhere is not
  // taken for ours.
  const dragType = 'application/x-pin-saved-overlay';
  let draggedId = $state<string | null>(null);
  // Set a frame after the drag starts, so the drag image still has the
  // row's contents
  let liftedId = $state<string | null>(null);
  let liftFrame = 0;
  let slotHeight = $state(0);
  let drop = $state<Drop | null>(null);
  // The message that had the focus when the drag started takes it back
  let refocusId: string | null = null;
  // A drop keeps the slot open until the server's list comes back, so the
  // messages do not jump back to where they were first. At most a second,
  // for a drop the server does not act on.
  let settling = false;
  let settleTimer = 0;

  const group = (id: string) => {
    const pin = saved.find((c) => c.id === id)?.pin ?? false;
    return saved.filter((c) => (c.pin ?? false) === pin);
  };

  // Where a drop puts the message, or null if it would stay where it is or
  // the anchor is in the other group. Counts places as the server does.
  const dropAt = (id: string, target: Drop): Drop | null => {
    const ids = group(id).map((c) => c.id);
    const from = ids.indexOf(id);
    const anchor = ids.indexOf(target.anchor);
    if (from === -1 || anchor === -1) {
      return null;
    }
    let to = target.position === 'after' ? anchor + 1 : anchor;
    if (from < to) {
      to -= 1;
    }
    return to === from ? null : target;
  };

  const dragStart = (event: DragEvent, id: string) => {
    const row = (event.currentTarget as HTMLElement).closest('.message');
    if (event.dataTransfer == null || row == null) {
      return;
    }
    dragEnd();
    draggedId = id;
    const rect = row.getBoundingClientRect();
    slotHeight = rect.height;
    liftFrame = requestAnimationFrame(() => (liftedId = id));
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(dragType, id);
    event.dataTransfer.setDragImage(
      row,
      event.clientX - rect.left,
      event.clientY - rect.top
    );
    // The focus outline would be in the drag image, next to the slot
    if (focusedId === id && document.activeElement instanceof HTMLElement) {
      refocusId = id;
      document.activeElement.blur();
    }
  };

  const dragEnd = () => {
    cancelAnimationFrame(liftFrame);
    clearTimeout(settleTimer);
    settling = false;
    draggedId = null;
    liftedId = null;
    drop = null;
    if (refocusId != null) {
      // Not from under whatever the moderator has focused since
      if (document.activeElement === document.body) {
        savedList
          ?.querySelector<HTMLElement>(`[data-id="${CSS.escape(refocusId)}"]`)
          ?.focus({ preventScroll: true });
      }
      refocusId = null;
    }
  };

  // The page gets no pointer events during a native drag, bar one in
  // Firefox with the button still down. A move with no button down is the
  // end of a drag whose dragend never came, which would leave the message
  // an empty frame.
  $effect(() => {
    if (draggedId == null) {
      return;
    }
    const lost = (event: PointerEvent) => {
      if (event.buttons === 0) {
        dragEnd();
      }
    };
    window.addEventListener('pointermove', lost);
    return () => window.removeEventListener('pointermove', lost);
  });

  // Whether the drag is ours. The browser can lose a dragend, as Firefox
  // does when the row moves in the page during its drag; the next drag
  // that is not ours clears what is left of it.
  const isOurs = (event: DragEvent) => {
    if (draggedId == null) {
      return false;
    }
    if (!event.dataTransfer?.types.includes(dragType)) {
      dragEnd();
      return false;
    }
    return true;
  };

  // Over a message, the upper half drops before it and the lower half
  // after. Below the last message, the drop goes after it. The slot belongs
  // to no message, so the pointer in it keeps the drop: the messages moving
  // apart under the pointer do not undo it. They also change the element
  // under a pointer that has stopped, which is a dragenter; the browser
  // refuses a drop right after one that is not cancelled.
  const dragOver = (event: DragEvent) => {
    if (!isOurs(event) || draggedId == null || savedList == null) {
      return;
    }
    event.preventDefault();
    const y = event.clientY;
    const rows = Array.from(
      savedList.querySelectorAll<HTMLElement>('[data-id]')
    );
    const rects = rows.map((row) => row.getBoundingClientRect());
    const at = (index: number, position: Drop['position']): Drop | null => {
      const anchor = rows[index]?.dataset.id;
      return anchor == null ? null : { position, anchor };
    };
    const over = rects.findIndex(
      (rect) => rect.height > 0 && y >= rect.top && y <= rect.bottom
    );
    const slot =
      drop == null ? -1 : rows.findIndex((r) => r.dataset.id === drop?.anchor);
    let next: Drop | null = null;
    if (over !== -1) {
      const rect = rects[over];
      next = at(over, y < rect.top + rect.height / 2 ? 'before' : 'after');
    } else if (
      drop != null &&
      slot !== -1 &&
      (drop.position === 'before'
        ? y >= rects[slot].top - slotHeight && y < rects[slot].top
        : y > rects[slot].bottom && y <= rects[slot].bottom + slotHeight)
    ) {
      next = drop;
    } else if (rects.length > 0 && y > rects[rects.length - 1].bottom) {
      next = at(rows.length - 1, 'after');
    }
    drop = next == null ? null : dropAt(draggedId, next);
    if (event.dataTransfer != null) {
      event.dataTransfer.dropEffect = drop == null ? 'none' : 'move';
    }
  };

  const dragLeave = (event: DragEvent) => {
    const section = event.currentTarget as HTMLElement;
    if (!settling && !section.contains(event.relatedTarget as Node | null)) {
      drop = null;
    }
  };

  const dropSaved = (event: DragEvent) => {
    if (!isOurs(event) || draggedId == null) {
      return;
    }
    event.preventDefault();
    // Another tab's drag of ours, over a drag here whose dragend was lost
    if (drop != null && event.dataTransfer?.getData(dragType) === draggedId) {
      overlaySocket?.emit('CHANGE', {
        type: 'OVERLAY_PLACE_CANDIDATE',
        id: draggedId,
        ...drop,
      });
      draggedId = null;
      settling = true;
      settleTimer = window.setTimeout(dragEnd, 1000);
      return;
    }
    dragEnd();
  };

  // The dragend that follows a drop leaves the slot to the server's list
  const dragEnded = () => {
    if (!settling) {
      dragEnd();
    }
  };

  const unarchive = (id: string) =>
    overlaySocket?.emit('CHANGE', { type: 'OVERLAY_UNARCHIVE', id });

  // Back on top of the saved messages, below the pinned unless it is one.
  const moveToSaved = (message: Overlay) => {
    overlaySocket?.emit('CHANGE', { type: 'OVERLAY_HIDE' });
    overlaySocket?.emit('CHANGE', {
      type: 'OVERLAY_SAVE_CANDIDATE',
      message,
    });
  };

  $effect(() => {
    if (overlaySocket != null) {
      const s = overlaySocket;
      const handler = (msg: AdminMessage) => {
        switch (msg.type) {
          case 'OVERLAY_STATE': {
            const before = saved;
            ({ live, candidates } = msg.state);
            keepFocus(before);
            if (settling) {
              dragEnd();
            }
            // A row taken out of the page during its drag gets no dragend
            if (draggedId != null && !saved.some((c) => c.id === draggedId)) {
              dragEnd();
            } else if (draggedId != null && drop != null) {
              drop = dropAt(draggedId, drop);
            }
            // admin-api pods from before the archive send none
            archive = msg.state.archive ?? [];
            break;
          }
        }
      };
      s.on('ADMIN', handler);
      return () => {
        s.off('ADMIN', handler);
      };
    }
  });
</script>

{#snippet username(user: Overlay['user'])}
  {#if onUserClick != null}
    <button class="username name-btn" onclick={() => onUserClick(user)}
      >{user.name}</button
    >:
  {:else}
    <span class="username">{user.name}:</span>
  {/if}
{/snippet}

<div class="container" class:presenter>
  <h2 class="header">Stream overlay</h2>
  <div class="live-section">
    <h3>
      Live
      {#if live != null}
        <span class="live"></span>
        <span class="live-seconds">{liveSeconds}s</span>
      {/if}
    </h3>
    {#each [live] as liveItem (liveItem?.id)}
      <div
        class:message={liveItem != null}
        class:text={liveItem == null}
        in:receive={{ key: liveItem?.id }}
        out:send={{ key: liveItem?.id }}
        animate:flip={{ duration: animationDuration }}
      >
        {#if liveItem != null}
          <div class="text">
            {@render username(liveItem.user)}
            <Message text={liveItem.text} />
          </div>
          <div class="actions">
            {#if !presenter}
              <button class="btn danger" onclick={() => moveToSaved(liveItem)}
                >Move to saved</button
              >
            {/if}
            <button class="btn danger" onclick={hide}>Hide</button>
          </div>
        {:else}
          No message is displayed.
        {/if}
      </div>
    {/each}
  </div>

  <div
    class="saved-section"
    role="presentation"
    ondragenter={presenter ? undefined : dragOver}
    ondragover={presenter ? undefined : dragOver}
    ondragleave={presenter ? undefined : dragLeave}
    ondrop={presenter ? undefined : dropSaved}
  >
    <h4>Saved</h4>
    {#if !presenter && saved.length > 1}
      <span class="hint">Drag to move, or click and use ↑ and ↓</span>
    {/if}
    <div
      class="list"
      role={saved.length > 0 ? 'list' : undefined}
      style:--slot-height="{slotHeight}px"
      bind:this={savedList}
    >
      {#each saved as c (c.id)}
        <!-- Focusable so the arrow keys can move it; the list has no
             interactive ARIA role for an item that holds buttons. -->
        <!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
        <div
          class="message"
          class:pinned={c.pin}
          class:movable={!presenter}
          class:lifted={liftedId === c.id}
          class:away={liftedId === c.id && drop != null}
          class:drop-before={drop?.anchor === c.id &&
            drop.position === 'before'}
          class:drop-after={drop?.anchor === c.id && drop.position === 'after'}
          role="listitem"
          tabindex={presenter ? undefined : 0}
          aria-keyshortcuts={presenter ? undefined : 'ArrowUp ArrowDown'}
          data-id={c.id}
          onkeydown={presenter ? undefined : (event) => moveSaved(event, c.id)}
          onfocusin={() => (focusedId = c.id)}
          onfocusout={() => {
            if (!reordering) focusedId = null;
          }}
          in:receive={{ key: c.id }}
          out:send={{ key: c.id }}
          animate:flip={{ duration: animationDuration }}
        >
          {#if presenter}
            <button
              class="btn favourite"
              class:active={c.pin}
              aria-label="Favourite"
              aria-pressed={c.pin ?? false}
              onclick={() => toggleOverlayPin(c.id)}
              ><Star active={c.pin} /></button
            >
          {:else}
            <!-- The arrow keys on the message do the same for the keyboard -->
            <span
              class="grip"
              draggable="true"
              title="Drag to move"
              aria-hidden="true"
              ondragstart={(event) => dragStart(event, c.id)}
              ondragend={dragEnded}><Grip /></span
            >
          {/if}
          <div class="text">
            {@render username(c.user)}
            <Message text={c.text} />
          </div>
          <div class="actions">
            {#if !presenter}
              <button class="btn danger" onclick={() => remove(c.id)}
                >Remove</button
              >
            {/if}
            <button class="btn success" onclick={() => show(c)}>Show</button>
            {#if !presenter}
              {#if c.pin}
                <button
                  class="btn"
                  title="Unpin from top"
                  onclick={() => toggleOverlayPin(c.id)}><Star active /></button
                >
              {:else}
                <button
                  class="btn"
                  title="Pin to top"
                  onclick={() => toggleOverlayPin(c.id)}><Star /></button
                >
              {/if}
            {/if}
          </div>
        </div>
      {:else}
        <div class="text">No saved messages.</div>
      {/each}
    </div>
  </div>

  <details class="archive-section">
    <summary><h4>Archive ({archive.length})</h4></summary>
    <div class="list">
      {#each archive as a (a.id)}
        <!-- No in:receive: a message leaving the air fades out where it is
             instead of flying into the archive. -->
        <div
          class="message"
          out:send={{ key: a.id }}
          animate:flip={{ duration: animationDuration }}
        >
          <div class="text">
            {@render username(a.user)}
            <Message text={a.text} />
          </div>
          <div class="actions">
            {#if !presenter}
              <button class="btn" onclick={() => unarchive(a.id)}
                >Unarchive</button
              >
            {/if}
            <button class="btn success" onclick={() => show(a)}>Show</button>
          </div>
        </div>
      {:else}
        <div class="text">Nothing has been on air yet.</div>
      {/each}
    </div>
  </details>
</div>

<style>
  .container {
    display: grid;
    grid-template-rows: 60px 1fr 3fr auto;
    height: 100%;
    padding: 0.5rem;
    gap: 1rem;
  }

  .header {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  h4 {
    padding: 8px;
    display: inline-block;
    margin-top: 4px;
  }

  .live-section {
    background: #1e2a2f;
    border-radius: 6px;
    flex: 1;
    overflow-y: auto;
  }

  .live-section h4 {
    margin: 0;
    background-color: #3a3a3a;
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
  }

  .live-section .live {
    margin-left: 1rem;
    border: 0.22em solid;
    border-radius: 50%;
    color: #e62421;
    display: inline-block;
    position: relative;
    vertical-align: middle;
  }

  .live-section .live-seconds {
    margin-left: 1rem;
    font-variant-numeric: tabular-nums;
    color: #a0a0a0;
    font-size: 0.9em;
  }

  .live-section .live::after,
  .live-section .live::before {
    animation: pulse 3s linear infinite;
    border: 0.1em solid;
    border-radius: 50%;
    box-sizing: border-box;
    content: '';
    height: 2em;
    margin: -1em;
    opacity: 0.1;
    position: absolute;
    width: 2em;
  }
  .live-section .live::after {
    animation-delay: -1.5s;
  }

  @media (prefers-reduced-motion: reduce) {
    .live-section .live::after,
    .live-section .live::before {
      animation: none;
    }
  }

  .saved-section {
    background: #1c1c1c;
    border-radius: 6px;
    flex: 1;
    overflow-y: auto;
  }
  .archive-section {
    background: #1c1c1c;
    border-radius: 6px;
  }

  .archive-section summary {
    cursor: pointer;
  }

  .archive-section summary h4 {
    margin: 0;
  }

  .archive-section .list {
    max-height: 40vh;
    overflow-y: auto;
  }

  .message {
    position: relative;
    border-bottom: 1px solid #333;
    padding: 6px 8px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #1c1c1c;
  }

  .message.pinned {
    background-color: #1e2a2f;
  }

  .hint {
    color: #a0a0a0;
    font-size: 0.8rem;
  }

  .message.movable {
    cursor: pointer;
  }

  .message.movable:focus-within {
    outline: 2px solid #64b5f6;
    outline-offset: -2px;
  }

  .grip {
    align-self: stretch;
    display: flex;
    align-items: center;
    margin: -6px 0 -6px -8px;
    padding: 0 4px 0 8px;
    color: #707070;
    cursor: grab;
  }

  .grip:hover {
    color: #e0e0e0;
  }

  /* The message picked up leaves an empty frame behind. Once the slot is
     open somewhere else the frame closes, so the list keeps its height: a
     taller list would get a scrollbar, and the messages would wrap and move
     under the pointer. */
  .message.lifted {
    outline: 1px dashed #555;
    outline-offset: -1px;
    background: none;
  }

  .message.lifted > * {
    visibility: hidden;
  }

  .message.lifted.away {
    height: 0;
    padding-block: 0;
    border-bottom-width: 0;
    outline: none;
    overflow: hidden;
  }

  /* The slot for the dragged message opens on the side of the message it
     would land at. It is margin, and the frame is drawn in it. */
  .message.drop-before {
    margin-top: var(--slot-height);
  }

  .message.drop-after {
    margin-bottom: var(--slot-height);
  }

  .message.drop-before::before,
  .message.drop-after::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: var(--slot-height);
    box-sizing: border-box;
    border: 2px solid #64b5f6;
    border-radius: 6px;
    background-color: rgb(100 181 246 / 12%);
  }

  .message.drop-before::before {
    bottom: 100%;
  }

  .message.drop-after::after {
    top: calc(100% + 1px);
  }

  .text {
    flex: 1;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .username {
    color: #64b5f6;
    font-weight: bold;
    font-size: 0.95rem;
    padding: 0;
    margin-right: 0.25rem;
  }

  .name-btn {
    background: none;
    border: none;
    cursor: pointer;
    margin-right: 0;
  }

  .name-btn:hover {
    text-decoration: underline;
  }

  .actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.25rem;
  }

  .btn {
    display: flex;
    background: transparent;
    border: 1px solid #444;
    border-radius: 6px;
    color: #e0e0e0;
    cursor: pointer;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    line-height: 1.2;
  }

  .btn.danger {
    background-color: var(--color-red-800);
  }

  .btn.success {
    background-color: var(--color-green-800);
  }

  .btn:hover {
    transform: translateY(-2px);
  }

  .btn:active {
    transform: translateY(2px);
  }

  .presenter .text,
  .presenter .username {
    font-size: 1.4rem;
  }

  .presenter .actions {
    gap: 1.5rem;
  }

  .presenter .btn {
    font-size: 1.05rem;
    padding: 0.5rem 0.75rem;
    min-height: 2.75rem;
    min-width: 2.75rem;
  }

  .presenter .message {
    padding: 1.5rem 0.75rem;
  }

  /* A favourite is a pinned message. The blue-grey of the pinned rows in the
     moderator view is the live section's colour, so here they get amber. The
     background is amber-400 at 14% over #1c1c1c, written out because
     color-mix() needs iOS 16.2 and the presenter's iPad may be on 15. */
  .presenter .message.pinned {
    background-color: #3c3218;
    box-shadow: inset 0.4rem 0 0 var(--color-amber-400);
  }

  .presenter .favourite {
    align-self: stretch;
    align-items: center;
    justify-content: center;
    color: #8a8a8a;
  }

  .presenter .favourite.active {
    border-color: var(--color-amber-400);
    color: var(--color-amber-400);
  }

  .presenter h4 {
    font-size: 1.3rem;
  }

  @keyframes pulse {
    0%,
    8% {
      opacity: 0;
      transform: scale3d(0.1, 0.1, 1);
    }
    15% {
      opacity: 1;
      transform: scale3d(0.1, 0.1, 1);
    }
    30% {
      opacity: 1;
      transform: scale3d(0.5, 0.5, 1);
    }
    to {
      opacity: 0;
      transform: scaleX(1);
    }
  }
</style>
